// Captain effects: every captain amplifies one thing and taxes another —
// the ship flies like its captain. Deterministic per temperament so players
// learn what each temperament means mechanically (FTL-pilot-style).

import type { CaptainRecord } from './seed.js';
import type { CaptainEffectMods } from '../types.js';

interface Pair {
  plus: keyof CaptainEffectMods;
  plusValue: number;
  minus: keyof CaptainEffectMods;
  minusValue: number;
  plusText: string;
  minusText: string;
}

const EFFECTS: Record<CaptainRecord['temperament'], Pair> = {
  stoic: {
    plus: 'shieldRegenMultBonus', plusValue: 0.15,
    minus: 'moveSpeedMult', minusValue: -0.05,
    plusText: '+15% SHIELD REGEN', minusText: '−5% MOVE SPEED',
  },
  hothead: {
    plus: 'damageMult', plusValue: 0.12,
    minus: 'maxHpDelta', minusValue: -10,
    plusText: '+12% DAMAGE', minusText: '−10 MAX HP',
  },
  smooth: {
    plus: 'magnetRadiusMult', plusValue: 0.15,
    minus: 'fireRateMult', minusValue: -0.08,
    plusText: '+15% MAGNET RADIUS', minusText: '−8% FIRE RATE',
  },
  worrier: {
    plus: 'maxHpDelta', plusValue: 10,
    minus: 'damageMult', minusValue: -0.08,
    plusText: '+10 MAX HP', minusText: '−8% DAMAGE',
  },
  deadpan: {
    plus: 'fireRateMult', plusValue: 0.1,
    minus: 'moveSpeedMult', minusValue: -0.05,
    plusText: '+10% FIRE RATE', minusText: '−5% MOVE SPEED',
  },
};

function zeroMods(): CaptainEffectMods {
  return {
    damageMult: 0,
    fireRateMult: 0,
    moveSpeedMult: 0,
    maxHpDelta: 0,
    magnetRadiusMult: 0,
    shieldRegenMultBonus: 0,
    dashHasteBonus: 0,
  };
}

/** The captain's perk/drawback pair as consumable stat mods. */
export function captainEffects(captain: CaptainRecord): CaptainEffectMods {
  const mods = zeroMods();
  const pair = EFFECTS[captain.temperament];
  mods[pair.plus] += pair.plusValue;
  mods[pair.minus] += pair.minusValue;
  return mods;
}

/** UI strings for the captain card ("+12% DAMAGE", "−10 MAX HP"). */
export function describeEffects(captain: CaptainRecord): { plus: string; minus: string } {
  const pair = EFFECTS[captain.temperament];
  return { plus: pair.plusText, minus: pair.minusText };
}
